import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass, vModelText as _vModelText, withDirectives as _withDirectives, resolveComponent as _resolveComponent, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, resolveDirective as _resolveDirective } from "vue"

const _hoisted_1 = { class: "title-inner log-action" }
const _hoisted_2 = { class: "title-inner-left" }
const _hoisted_3 = ["disabled"]
const _hoisted_4 = { class: "title-inner-right" }
const _hoisted_5 = {
  class: "status log-action text-center p-10",
  style: {"min-width":"80px"}
}
const _hoisted_6 = { class: "log-action ml-5" }
const _hoisted_7 = ["placeholder"]
const _hoisted_8 = { class: "log-action ml-5" }
const _hoisted_9 = {
  class: "fixed",
  cellpadding: "0",
  cellspacing: "0"
}
const _hoisted_10 = { class: "logs-body" }
const _hoisted_11 = { class: "time" }
const _hoisted_12 = { class: "msg" }
const _hoisted_13 = { key: 1 }
const _hoisted_14 = {
  colspan: "2",
  class: "msg text-muted"
}
const _hoisted_15 = { key: 2 }
const _hoisted_16 = {
  colspan: "2",
  class: "msg text-muted"
}

import { useStore } from 'vuex';
import { 
  ref, 
  onMounted, 
  onBeforeUnmount, 
  computed, 
  nextTick, 
  PropType,
} from 'vue';

import Socket, {
  EVENT_CONNECTED,
  EVENT_DISCONNECTED,
  EVENT_MESSAGE,
  EVENT_CONNECT_ERROR
} from '@shell/utils/socket';
import day from 'dayjs';
import AnsiUp from 'ansi_up';
import { addParams } from '@shell/utils/url';
import { downloadFile } from '@shell/utils/download';
import { escapeHtml, escapeRegex } from '@shell/utils/string';
import { LOGS_TIME, LOGS_WRAP, DATE_FORMAT, TIME_FORMAT } from '@shell/store/prefs';

import Select from '@shell/components/form/Select';
import { Checkbox } from '@components/Form/Checkbox';
import AsyncButton from '@shell/components/AsyncButton';
import Window from '@shell/components/nav/WindowManager/Window';

import { useApplicationSocketMixin } from './ApplicationSocketMixin';


export default /*@__PURE__*/_defineComponent({
  __name: 'ApplicationLogs',
  props: {
  application: {
    type: Object as PropType<object>,
    default: null,
  },
  endpoint: {
    type: String as PropType<string>,
    default: '',
  },
  ansiToHtml: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
  initialInstance: {
    type:    String,
    default: null,
  },
},
  setup(__props) {

const store = useStore();
const t = store.getters['i18n/t'];

const props = __props;

const {
  socket,
  isOpen,
  backlog,
  instanceChoices,
  getRootSocketUrl,
} = useApplicationSocketMixin(props);

const lastId = ref<number>(1);
const search = ref<string>('');
const instance = ref<string>(props.initialInstance || instanceChoices[0]);
const lines = ref<Array<any>>([]);
const timerFlush = ref<object>(null);
const isFollowing = ref<boolean>(false);
const active = ref<boolean>(true);
const body = ref<HTMLElement>(null);

const ansiup = new AnsiUp();
const timestamps = store.getters['prefs/get'](LOGS_TIME);
const wrap = ref<boolean>(store.getters['prefs/get'](LOGS_WRAP));

onMounted(async () => {
  await connect();
  let boundUpdateFollowing = updateFollowing.bind(body);
  body.value.addEventListener('scroll', boundUpdateFollowing);
  timerFlush.value = setInterval(flush, 100);
});

onBeforeUnmount(() => {
  const el = document.querySelector('.logs-container') as HTMLElement;
  if (el) {
    el.removeEventListener('scroll', updateFollowing);
  }
  cleanup();
  isOpen.value = false;
});

const instanceChoicesWithNone = computed(() => {
  return [
    ...instanceChoices.value,
    {
      label: 'No Instance Filter',
      value: null
    }
  ];
});

const filtered = computed(() => {
  if (!search.value && !instance.value) {
    return lines.value;
  }

  const re = new RegExp(escapeRegex(search.value), 'img');
  const out = [];

  for (const line of lines.value) {
    let msg = line.rawMsg;

    if (instance.value) {
      const pod = msg.substring(1, msg.length);

      if (!pod.startsWith(instance.value)) {
        continue;
      }
    }

    const matches = msg.match(re);

    if (!matches) {
      continue;
    }

    const parts = msg.split(re);

    msg = '';
    while (parts.length || matches.length) {
      if (parts.length) {
        msg += ansiup.ansi_to_html(parts.shift()); // This also escapes
      }

      if (matches.length) {
        msg += `<span class="highlight">${ ansiup.ansi_to_html(matches.shift()) }</span>`;
      }
    }

    out.push({
      id:   line.id,
      time: line.time,
      msg,
    });
  }

  return out;
});

const timeFormatStr = computed(() => {
  const dateFormat = escapeHtml(store.getters['prefs/get'](DATE_FORMAT));
  const timeFormat = escapeHtml(store.getters['prefs/get'](TIME_FORMAT));

  return `${ dateFormat } ${ timeFormat }`;
});

const getSocketUrl = async () => {
  const { url, token } = await getRootSocketUrl();
  return addParams(url, { follow: true, authtoken: token });
};

const connect = async () => {
  if (socket.value) {
    await socket.value.disconnect();
    socket.value = null;
    lines.value = [];
  }

  lines.value = [];

  const url = await getSocketUrl();
  socket.value = new Socket(url, true, 0);
  socket.value.setAutoReconnectUrl(async() => {
    return await getSocketUrl();
  });

  socket.value.addEventListener(EVENT_CONNECTED, () => {
    isOpen.value = true;
  });

  socket.value.addEventListener(EVENT_DISCONNECTED, () => {
    isOpen.value = false;
  });

  socket.value.addEventListener(EVENT_CONNECT_ERROR, (e: any) => {
    isOpen.value = false;
    console.error('Connect Error', e);
  });

  socket.value.addEventListener(EVENT_MESSAGE, async (e: any) => {
    let parsedData;
    
    try {
      parsedData = JSON.parse(e.detail.data);
    } catch (e) {
      console.warn('Unable to parse websocket data: ', e);
      return;
    }

    const { PodName, Message } = parsedData;

    const line = `[${ PodName }] ${ Message }`;
    backlog.value.push({
      id:     lastId.value++,
      msg:    props.ansiToHtml ? ansiup.ansi_to_html(line) : line,
      rawMsg: line,
    });
  });
  
  socket.value.connect();
};

const flush = () => {
  if (backlog.value.length) {
    lines.value.push(...backlog.value);
    backlog.value = [];
  }

  if (isFollowing.value) {
    nextTick(() => {
      follow();
    });
  }
};

const updateFollowing = () => {
  const el = body.value;

  isFollowing.value = el.scrollTop + el.clientHeight + 2 >= el.scrollHeight;
};

const clear = () => {
  lines.value = [];
};

const download = (btnCb) => {
  const date = new Date().toISOString().split('.')[0];
  const fileName = `${ props.application.nameDisplay }-${ date }`;

  downloadFile(fileName, lines.value.map((l) => `${ l.rawMsg }`).join('\n'))
    .then(() => btnCb(true))
    .catch(() => btnCb(false));
};

const follow = () => {
  body.value.scrollTop = body.value.scrollHeight;
};

const toggleWrap = () => {
  store.dispatch('prefs/set', { key: LOGS_WRAP, value: wrap.value });
};

const format = (time) => {
  if (!time) {
    return '';
  }

  return day(time).format(timeFormatStr.value);
};

const cleanup = () => {
  if (socket.value) {
    socket.value.disconnect();
    socket.value = null;
  }

  clearInterval(timerFlush.value);
};

return (_ctx: any,_cache: any) => {
  const _component_VDropdown = _resolveComponent("VDropdown")!
  const _directive_clean_html = _resolveDirective("clean-html")!

  return (_openBlock(), _createBlock(_unref(Window), {
    active: active.value,
    "before-close": cleanup,
    class: "epinio-app-log"
  }, {
    title: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          (_unref(instanceChoices).length > 1)
            ? (_openBlock(), _createBlock(_unref(Select), {
                key: 0,
                value: instance.value,
                "onUpdate:value": _cache[0] || (_cache[0] = ($event: any) => ((instance).value = $event)),
                disabled: _unref(instanceChoices).length === 1,
                class: "containerPicker auto-width",
                options: instanceChoicesWithNone.value,
                clearable: true,
                placement: "top",
                placeholder: "Filter by Instance"
              }, null, 8, ["value", "disabled", "options"]))
            : _createCommentVNode("", true),
          _cache[3] || (_cache[3] = _createTextVNode()),
          _createElementVNode("button", {
            class: "btn bg-primary ml-5",
            disabled: isFollowing.value,
            onClick: follow
          }, _toDisplayString(_unref(t)('wm.containerLogs.follow')), 9, _hoisted_3),
          _cache[4] || (_cache[4] = _createTextVNode()),
          _createElementVNode("button", {
            class: "btn bg-primary ml-5",
            onClick: clear
          }, _toDisplayString(_unref(t)('wm.containerLogs.clear')), 1),
          _cache[5] || (_cache[5] = _createTextVNode()),
          _createVNode(_unref(AsyncButton), {
            class: "ml-5",
            mode: "download",
            onClick: download
          })
        ]),
        _cache[10] || (_cache[10] = _createTextVNode()),
        _cache[11] || (_cache[11] = _createElementVNode("div", { style: {"flex":"1"} }, null, -1)),
        _cache[12] || (_cache[12] = _createTextVNode()),
        _createElementVNode("div", _hoisted_4, [
          _createElementVNode("div", _hoisted_5, [
            _createElementVNode("span", {
              class: _normalizeClass({'text-success': _unref(isOpen), 'text-error': !_unref(isOpen)})
            }, _toDisplayString(_unref(t)(_unref(isOpen) ? 'wm.connection.connected' : 'wm.connection.disconnected')), 3)
          ]),
          _cache[8] || (_cache[8] = _createTextVNode()),
          _createElementVNode("div", _hoisted_6, [
            _withDirectives(_createElementVNode("input", {
              "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((search).value = $event)),
              class: "input-sm",
              type: "search",
              placeholder: _unref(t)('wm.containerLogs.search')
            }, null, 8, _hoisted_7), [
              [_vModelText, search.value]
            ])
          ]),
          _cache[9] || (_cache[9] = _createTextVNode()),
          _createElementVNode("div", _hoisted_8, [
            _createVNode(_component_VDropdown, { placement: "top" }, {
              popper: _withCtx(() => [
                _createVNode(_unref(Checkbox), {
                  value: wrap.value,
                  "onUpdate:value": [
                    _cache[2] || (_cache[2] = ($event: any) => ((wrap).value = $event)),
                    toggleWrap
                  ],
                  label: _unref(t)('wm.containerLogs.wrap')
                }, null, 8, ["value", "label"])
              ]),
              default: _withCtx(() => [
                _cache[6] || (_cache[6] = _createElementVNode("button", { class: "btn bg-primary" }, [
                  _createElementVNode("i", { class: "icon icon-gear" })
                ], -1)),
                _cache[7] || (_cache[7] = _createTextVNode())
              ]),
              _: 1,
              __: [6,7]
            })
          ])
        ])
      ])
    ]),
    body: _withCtx(() => [
      _createElementVNode("div", {
        ref_key: "body",
        ref: body,
        class: _normalizeClass({'logs-container': true, 'open': _unref(isOpen), 'closed': !_unref(isOpen), 'show-times': _unref(timestamps) && filtered.value.length, 'wrap-lines': wrap.value})
      }, [
        _createElementVNode("table", _hoisted_9, [
          _createElementVNode("tbody", _hoisted_10, [
            (filtered.value.length)
              ? (_openBlock(true), _createElementBlock(_Fragment, { key: 0 }, _renderList(filtered.value, (line) => {
                  return (_openBlock(), _createElementBlock("tr", {
                    key: line.id
                  }, [
                    _withDirectives(_createElementVNode("td", _hoisted_11, null, 512), [
                      [_directive_clean_html, format(line.time)]
                    ]),
                    _cache[13] || (_cache[13] = _createTextVNode()),
                    _withDirectives(_createElementVNode("td", _hoisted_12, null, 512), [
                      [_directive_clean_html, line.msg]
                    ])
                  ]))
                }), 128))
              : (search.value)
                ? (_openBlock(), _createElementBlock("tr", _hoisted_13, [
                    _createElementVNode("td", _hoisted_14, _toDisplayString(_unref(t)('wm.containerLogs.noMatch')), 1)
                  ]))
                : (_openBlock(), _createElementBlock("tr", _hoisted_15, [
                    _createElementVNode("td", _hoisted_16, _toDisplayString(_unref(t)('epinio.applications.wm.noData')), 1)
                  ]))
          ])
        ])
      ], 2)
    ]),
    _: 1,
    __: [14]
  }, 8, ["active"]))
}
}

})